import { AiccLanguageStatus } from "constants/aiccLanguage";

export type AiccVoiceGender = "male" | "female";

export interface AiccAssetVersionGroup<T> {
  status: AiccLanguageStatus;
  items: T[];
  ttsModel?: string;
}

export interface AiccAssetVersions<T> {
  versions: AiccAssetVersionGroup<T>[];
  hasTransliteration?: boolean;
  filters?: AiccLessonLevel[];
  ttsModelOptions?: AiccTtsModelOption[];
}

export interface AiccTtsModelOption {
  id: string;
  displayName: string;
  supportsStreamPlay: boolean;
}

export interface AiccAssetApi<T, P> {
  fetchVersions: (languageId: number) => Promise<AiccAssetVersions<T>>;
  create: (languageId: number, payload: P) => Promise<AiccAssetVersions<T>>;
  update: (languageId: number, itemId: string, payload: P) => Promise<AiccAssetVersions<T>>;
  remove: (languageId: number, itemId: string) => Promise<AiccAssetVersions<T>>;
}

export interface AiccDefaultScenario {
  id: string;
  title: string;
  learnerContextDescription: string;
  aiContextDescription: string;
  voiceGender: AiccVoiceGender;
}

export interface AiccDefaultScenarioPayload {
  title: string;
  learnerContextDescription: string;
  aiContextDescription: string;
  voiceGender: AiccVoiceGender;
}

export interface AiccScenarioCategory {
  id: string;
  name: string;
  description: string;
}

export interface AiccScenarioCategoryPayload {
  name: string;
  description: string;
}

export const AICC_STT_MODE = {
  realtime: "REALTIME",
  batch: "BATCH",
} as const;

export type AiccSttMode = typeof AICC_STT_MODE[keyof typeof AICC_STT_MODE];

export const AICC_STT_ROLE = {
  primary: "primary",
  fallback: "fallback",
} as const;

export type AiccSttRole = typeof AICC_STT_ROLE[keyof typeof AICC_STT_ROLE];

export interface AiccSttOption {
  key: string;
  displayName: string;
  mode: AiccSttMode;
  description: string;
  /** Which slot runs this engine when a language configures none — the backend names it, not this repo. */
  isDefaultPrimary: boolean;
  isDefaultFallback: boolean;
  /**
   * On its way out: still selectable and still built, but not what a new configuration should choose. Read
   * only when this screen picks the other slot's engine for the operator, which prefers a current one.
   */
  isDeprecated: boolean;
}

export interface AiccSttSettingRow {
  id: AiccSttRole;
  optionKey?: string;
}

export interface AiccSttSettingPayload {
  primaryKey?: string;
  fallbackKey?: string;
}

export interface AiccSttSettingApi {
  fetchVersions: (languageId: number) => Promise<AiccAssetVersions<AiccSttSettingRow>>;
  update: (languageId: number, payload: AiccSttSettingPayload) => Promise<AiccAssetVersions<AiccSttSettingRow>>;
}

export const AICC_TTS_TARGET = {
  main: "main",
  fallback: "fallback",
} as const;

export const AICC_TTS_PROVIDER = "elevenlabs";

export const AICC_TTS_MODEL = {
  flashV2_5: "eleven_flash_v2_5",
  v3: "eleven_v3",
  v4: "eleven_v4",
} as const;

export type AiccTtsTarget = typeof AICC_TTS_TARGET[keyof typeof AICC_TTS_TARGET];

export interface AiccTtsVoice {
  voiceId?: string;
  baseSpeed?: number;
}

export interface AiccTtsVoiceRow {
  id: string;
  main: AiccTtsVoice;
  fallback: AiccTtsVoice;
}

export interface AiccTtsPreviewPayload {
  voiceId: string;
  text: string;
  baseSpeed: number;
  model?: string;
  /** The model's `supportsStreamPlay`, from the backend's options; undefined when it sent none. */
  streaming?: boolean;
}

export interface AiccTtsSettingApi {
  fetchVersions: (languageId: number) => Promise<AiccAssetVersions<AiccTtsVoiceRow>>;
  update: (
    languageId: number,
    rows: AiccTtsVoiceRow[],
    selectedModel?: string,
    savedModel?: string
  ) => Promise<AiccAssetVersions<AiccTtsVoiceRow>>;
  synthesize: (languageId: number, payload: AiccTtsPreviewPayload) => Promise<string>;
}

export interface AiccUiElementTranslationRow {
  id: string;
  translation?: string;
}

export interface AiccUiElementTranslationApi {
  fetchVersions: (languageId: number) => Promise<AiccAssetVersions<AiccUiElementTranslationRow>>;
  update: (
    languageId: number,
    rows: AiccUiElementTranslationRow[]
  ) => Promise<AiccAssetVersions<AiccUiElementTranslationRow>>;
}

export interface AiccLanguageName {
  id: number;
  name: string;
}

export interface AiccLessonLevel {
  level: number;
  lessons: number[];
}

export interface AiccLessonEntryRef {
  level: number;
  lesson: number;
  index: number;
}

export interface AiccLessonEntry extends AiccLessonEntryRef {
  id: string;
  text: string;
  translation: string;
  transliteration?: string;
}

export interface AiccLessonEntryPayload {
  level: number;
  lesson: number;
  text: string;
  translation: string;
  transliteration?: string;
}

export interface AiccLessonRef {
  level: number;
  lesson: number;
}

export interface AiccAssetUploadIssue {
  type: string;
  message: string;
  row?: number;
}

export interface AiccAssetUploadPreview<T> {
  isValid: boolean;
  errors: AiccAssetUploadIssue[];
  rows: T[];
}

export interface AiccLessonEntryUploadPreview extends AiccAssetUploadPreview<AiccLessonEntryPayload> {
  hasTransliteration: boolean;
}

export interface AiccLessonEntryApi {
  fetchVersions: (languageId: number) => Promise<AiccAssetVersions<AiccLessonEntry>>;
  create: (languageId: number, payload: AiccLessonEntryPayload) => Promise<AiccAssetVersions<AiccLessonEntry>>;
  update: (
    languageId: number,
    ref: AiccLessonEntryRef,
    payload: AiccLessonEntryPayload
  ) => Promise<AiccAssetVersions<AiccLessonEntry>>;
  remove: (languageId: number, ref: AiccLessonEntryRef) => Promise<AiccAssetVersions<AiccLessonEntry>>;
  validateUpload: (languageId: number, file: File) => Promise<AiccLessonEntryUploadPreview>;
  replaceAll: (languageId: number, items: AiccLessonEntryPayload[]) => Promise<AiccAssetVersions<AiccLessonEntry>>;
  exportFile: (languageId: number, status: AiccLanguageStatus) => Promise<Blob>;
}

export interface AiccReviewScriptLine {
  speaker: string;
  cue: string;
  text: string;
  transliteration?: string;
}

export interface AiccReviewScript {
  scriptIndex: number;
  title: string;
  sceneSetup: string;
  // null means the script records no gender, which the backend resolves to its default voice.
  rolePlayerGender: string | null;
  coversPhrases: string[];
  lines: AiccReviewScriptLine[];
}

export interface AiccLessonReviewScript extends AiccLessonRef {
  id: string;
  scripts: AiccReviewScript[];
}

export type AiccLessonReviewScriptUploadPreview = AiccAssetUploadPreview<AiccLessonReviewScript>;

export interface AiccLessonReviewScriptApi {
  fetchVersions: (languageId: number) => Promise<AiccAssetVersions<AiccLessonReviewScript>>;
  validateUpload: (languageId: number, file: File) => Promise<AiccLessonReviewScriptUploadPreview>;
  replaceAll: (
    languageId: number,
    lessons: AiccLessonReviewScript[]
  ) => Promise<AiccAssetVersions<AiccLessonReviewScript>>;
}

export interface AiccCanDoStatement {
  id: string;
  fluencyLevel: number;
  statement: string;
}

export interface AiccCanDoStatementPayload {
  fluencyLevel: number;
  statement: string;
}

export interface AiccCanDoStatementUploadRow extends AiccCanDoStatementPayload {
  id?: string;
}

export type AiccCanDoStatementUploadPreview = AiccAssetUploadPreview<AiccCanDoStatementUploadRow>;

export interface AiccCanDoStatementApi extends AiccAssetApi<AiccCanDoStatement, AiccCanDoStatementPayload> {
  validateUpload: (languageId: number, file: File) => Promise<AiccCanDoStatementUploadPreview>;
  replaceAll: (
    languageId: number,
    rows: AiccCanDoStatementUploadRow[]
  ) => Promise<AiccAssetVersions<AiccCanDoStatement>>;
}

export interface AiccLessonInformationPayload {
  summary: string;
  keyVocabulary: string;
}

export interface AiccLessonInformation extends AiccLessonRef, AiccLessonInformationPayload {
  id: string;
}

export type AiccLessonInformationUploadPreview = AiccAssetUploadPreview<AiccLessonInformation>;

export interface AiccLessonInformationApi {
  fetchVersions: (languageId: number) => Promise<AiccAssetVersions<AiccLessonInformation>>;
  update: (
    languageId: number,
    ref: AiccLessonRef,
    payload: AiccLessonInformationPayload
  ) => Promise<AiccAssetVersions<AiccLessonInformation>>;
  validateUpload: (languageId: number, file: File) => Promise<AiccLessonInformationUploadPreview>;
  replaceAll: (
    languageId: number,
    lessons: AiccLessonInformation[]
  ) => Promise<AiccAssetVersions<AiccLessonInformation>>;
}

export interface LessonEntryAssetConfig {
  title: string;
  textLabel: string;
  addText: string;
  exportName: string;
  fieldGuideIntro: string;
  hasAgenticAssetLink?: boolean;
  api: AiccLessonEntryApi;
}
